import { useCallback, useEffect, useRef } from 'react'

interface Props {
	before: string
	after: string
	beforeLabel?: string
	afterLabel?: string
	className?: string
	/** aspect-ratio стейджа (напр. '9 / 16'); по умолчанию из SCSS (4/3). */
	aspectRatio?: string
}

/**
 * Before/After слайдер сравнения (как airbrush): два непрозрачных изображения
 * друг на друге, верхнее обрезается по вертикали через clip-path.
 *
 * Производительность (без глюков/лагов):
 *   • позиция стыка держится в CSS-переменной --x, обновляемой ИМПЕРАТИВНО
 *     (setProperty) — ноль React-ре-рендеров на движение ползунка;
 *   • обновление throttled через requestAnimationFrame;
 *   • pointer capture — драг не теряется, если палец/курсор ушёл за край;
 *   • touch-action: none (в SCSS) — иначе тач-драг конфликтует со скроллом.
 * Дефолт --x=50% задан в SCSS, поэтому смена src (вариации) не сбрасывает позицию.
 */
export default function BeforeAfterSlider({
	before,
	after,
	beforeLabel = 'До',
	afterLabel = 'После',
	className,
	aspectRatio,
}: Props) {
	const rootRef = useRef<HTMLDivElement>(null)
	const draggingRef = useRef(false)
	const rafRef = useRef<number | null>(null)
	const pendingRef = useRef<number | null>(null)

	const applyX = useCallback((clientX: number) => {
		const el = rootRef.current
		if (!el) return
		const r = el.getBoundingClientRect()
		const pct = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100))
		pendingRef.current = pct
		if (rafRef.current == null) {
			rafRef.current = requestAnimationFrame(() => {
				rafRef.current = null
				if (pendingRef.current != null && rootRef.current) {
					rootRef.current.style.setProperty('--x', `${pendingRef.current}%`)
				}
			})
		}
	}, [])

	const onPointerDown = (e: React.PointerEvent) => {
		draggingRef.current = true
		;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
		applyX(e.clientX)
	}
	const onPointerMove = (e: React.PointerEvent) => {
		if (draggingRef.current) applyX(e.clientX)
	}
	const onPointerUp = (e: React.PointerEvent) => {
		draggingRef.current = false
		;(e.currentTarget as Element).releasePointerCapture?.(e.pointerId)
	}

	// Клавиатура (доступность): ← → двигают стык на 4%.
	const onKeyDown = (e: React.KeyboardEvent) => {
		const el = rootRef.current
		if (!el) return
		const cur = parseFloat(getComputedStyle(el).getPropertyValue('--x')) || 50
		if (e.key === 'ArrowLeft') {
			el.style.setProperty('--x', `${Math.max(0, cur - 4)}%`)
			e.preventDefault()
		} else if (e.key === 'ArrowRight') {
			el.style.setProperty('--x', `${Math.min(100, cur + 4)}%`)
			e.preventDefault()
		}
	}

	useEffect(
		() => () => {
			if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
		},
		[],
	)

	return (
		<div
			ref={rootRef}
			className={`ba-slider${className ? ` ${className}` : ''}`}
			style={aspectRatio ? { aspectRatio } : undefined}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
			onKeyDown={onKeyDown}
			role="slider"
			aria-label="Сравнение до и после — потяните"
			aria-valuemin={0}
			aria-valuemax={100}
			tabIndex={0}
		>
			{/* нижний слой — «после» (результат), целиком. Кадры вариаций
			    прелоажены в модале → смена src мгновенна, без вспышки. */}
			<img className="ba-slider__img ba-slider__after" src={after} alt="" draggable={false} />
			{/* верхний слой — «до», обрезан по --x */}
			<img className="ba-slider__img ba-slider__top" src={before} alt="" draggable={false} />

			<span className="ba-slider__label ba-slider__label--before">{beforeLabel}</span>
			<span className="ba-slider__label ba-slider__label--after">{afterLabel}</span>

			<div className="ba-slider__divider" aria-hidden="true">
				<span className="ba-slider__handle">
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="15 6 9 12 15 18" />
						<polyline points="9 6 15 12 9 18" />
					</svg>
				</span>
			</div>
		</div>
	)
}
